// crewbit-admin/src/hooks/useUpdateBusinessMoneySettings.ts
//
// Session 137 §Settings hub substrate — Money Module RPC-routed business
// settings mutations (admin / operator).
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Settings.5 + §Settings.6.
// Mirrors crewbit-mobile/hooks/useUpdateBusinessMoneySettings.ts.
//
// Single RPC writes BOTH businesses (tax_mode) AND business_financial_settings
// (everything else) atomically per §3c. Activity_log writer fires only on
// actual tax_mode change. Owner OR manager role enforced server-side.

import { useCallback } from 'react';
import { callMoneyRpc, type MoneyRpcResult } from '../lib/moneyRpc';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';

export type TaxMode = 'charge' | 'exempt' | 'quick_method';

export interface UpdateBusinessMoneySettingsArgs {
  businessId: string;
  taxMode?: TaxMode | null;
  hstNumber?: string | null;
  fiscalYearStart?: number | null;
  defaultPaymentTerms?: number | null;
  defaultDepositPercentage?: number | null;
  mileageRate1?: number | null;
  mileageRate2?: number | null;
  mileageThreshold?: number | null;
  // Tier 2 Phase C §WorldClass.2 (Migration 274): operator escape hatch for
  // Quick Method rate. Decimal 0..1 exclusive. NULL = use derived rate.
  quickMethodRemittanceRateOverride?: number | null;
  // Tier 2 Phase E §WorldClass.5 (Migration 276): closes §Settings.TipsEnabledRpcParam.
  // Activates customer-side tips selector at hosted-page payment time.
  tipsEnabled?: boolean | null;
  // §Wave-A Phase C (Migration 284): closes §FinancialSettings.LegacyDirectWriteRetirement.
  // 4 operator-domain numerics absorbed from the retired legacy financial
  // settings section's direct upsert path. hstRate decimal 0..1 (e.g.,
  // 0.13). vehicle/telecom/homeOffice
  // percentages 0..100. All optional / nullable; NULL preserves existing BFS value.
  hstRate?: number | null;
  vehicleBusinessUse?: number | null;
  telecomBusinessUse?: number | null;
  homeOfficePercentage?: number | null;
  // Today Hub Spec v1 §15 PD-1 (migration 20261103232558): CRA filing cadence.
  // NULL preserves the existing value. Mirrors the mobile hook.
  hstFilingFrequency?: HstFilingFrequency | null;
}

export type HstFilingFrequency = 'monthly' | 'quarterly' | 'annual';

export type UpdateBusinessMoneySettingsResult = MoneyRpcResult<{
  business_id: string;
  updated_fields: string[];
  tax_mode_changed: boolean;
}>;

export function useUpdateBusinessMoneySettings() {
  return useCallback(
    async (args: UpdateBusinessMoneySettingsArgs): Promise<UpdateBusinessMoneySettingsResult> => {
      const result = await callMoneyRpc<{
        business_id: string;
        updated_fields: string[];
        tax_mode_changed: boolean;
      }>('update_business_money_settings', {
        p_business_id: args.businessId,
        p_tax_mode: args.taxMode,
        p_hst_number: args.hstNumber,
        p_fiscal_year_start: args.fiscalYearStart,
        p_default_payment_terms: args.defaultPaymentTerms,
        p_default_deposit_percentage: args.defaultDepositPercentage,
        p_mileage_rate_1: args.mileageRate1,
        p_mileage_rate_2: args.mileageRate2,
        p_mileage_threshold: args.mileageThreshold,
        p_quick_method_remittance_rate_override: args.quickMethodRemittanceRateOverride,
        p_tips_enabled: args.tipsEnabled,
        p_hst_rate: args.hstRate,
        p_vehicle_business_use: args.vehicleBusinessUse,
        p_telecom_business_use: args.telecomBusinessUse,
        p_home_office_percentage: args.homeOfficePercentage,
        p_hst_filing_frequency: args.hstFilingFrequency,
      });
      if (result.success) {
        window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
      }
      return result;
    },
    [],
  );
}
